import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/utils/api";
import { CustomerTags } from "./CustomerTags";
import { CustomerNotes } from "./CustomerNotes";
import { CustomerMetadata } from "./CustomerMetadata";
import { useCurrentStore } from "@/hooks/useCurrentStore";

interface CustomerDetailsProps {
  customerId: string;
}

export function CustomerDetails({ customerId }: CustomerDetailsProps) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const { currentStore } = useCurrentStore();

  const { data: customer, isLoading } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      const { data } = await api.get(`/api/customers/${customerId}`);
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (updatedData: any) => {
      const { data } = await api.patch(
        `/api/customers/${customerId}`,
        updatedData
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["customer", customerId] });
      setIsEditing(false);
      toast.success("Customer updated successfully");
    },
    onError: () => {
      toast.error("Failed to update customer");
    },
  });

  const getCurrencySymbol = () => {
    return currentStore?.currency || "$";
  };

  if (isLoading) return <div className="text-center py-8 text-gray-500">Loading...</div>;
  if (!customer) return <div className="text-center py-8 text-gray-500">Customer not found</div>;

  return (
    <div className="space-y-6">
      {/* Customer Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="p-3">
          <div className="text-sm text-gray-500">Total Orders</div>
          <div className="text-lg font-bold">{customer.orderCount || 0}</div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-gray-500">Total Spent</div>
          <div className="text-lg font-bold">
            {getCurrencySymbol()}
            {(customer.totalSpent || 0).toFixed(2)}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-gray-500">Last Order</div>
          <div className="text-lg font-semibold">
            {customer.lastOrder
              ? format(new Date(customer.lastOrder), "MMM d, yyyy")
              : "Never"}
          </div>
        </Card>
        <Card className="p-3">
          <div className="text-sm text-gray-500">Member Since</div>
          <div className="text-lg font-semibold">
            {format(new Date(customer.createdAt), "MMM d, yyyy")}
          </div>
        </Card>
      </div>

      <Card className="p-4 !gap-0">
        <div className="flex justify-between items-center mb-4">
          <p className="text-sm font-semibold">Customer Information</p>
          {/*
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? 'Cancel' : 'Edit'}
          </Button>
          */}
        </div>

        {isEditing ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              updateMutation.mutate(Object.fromEntries(formData));
            }}
            className="space-y-4"
          >
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input name="name" defaultValue={customer.name} required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <Input
                name="email"
                type="email"
                defaultValue={customer.email}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <Input name="phone" defaultValue={customer.phone} />
            </div>
            <Button type="submit">Save Changes</Button>
          </form>
        ) : (
          <div className="space-y-4 m-0 p-0">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <p>{customer.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <p>{customer.email}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <p>{customer.phone}</p>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4 !gap-0">
        <p className="text-sm font-semibold">Order History</p>
        {customer.orders && customer.orders.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customer.orders.map((order: any) => (
                <TableRow key={order._id}>
                  <TableCell>
                    {order.orderNumber || order._id.slice(-6)}
                  </TableCell>
                  <TableCell>
                    {format(new Date(order.createdAt), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={getStatusVariant(order.status)}
                      className="capitalize"
                    >
                      {order.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {getCurrencySymbol()}
                    {(order.pricing?.total || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <Link
                      to={`/dashboard/orders/${order._id}`}
                      className="text-primary hover:underline"
                    >
                      View Order
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No orders found for this customer.
          </div>
        )}
      </Card>
    </div>
  );
}

function getStatusVariant(
  status: string
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "pending":
      return "outline";
    case "processing":
      return "default";
    case "shipped":
      return "secondary";
    case "delivered":
      return "default";
    case "cancelled":
    case "refunded":
      return "destructive";
    default:
      return "outline";
  }
}
